/**
 * Vehicle alert-stations hook for the VehicleDetail deep-dive page (PR 5.5).
 *
 * Hits `GET /api/v1/vehicles/by-token/:asset_token/alert-stations` —
 * returns the top N stations where this vehicle has the most fraud
 * alerts in the selected period (NOT the most transactions — see the
 * docstring on the backend SQL helper for the rationale). Backs both
 * the EntityLocationMap and the compact stations table on the page.
 *
 * Cache key includes period + limit so toggling the time selector or
 * the row count flips to a fresh query. The `enabled` flag stops the
 * hook firing when the route param is the literal placeholder during
 * the initial render.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Period } from '@/lib/timePeriod';
import type { VehicleAlertStationsResponse } from '@/types/vehicles';

const STALE_MS = 30_000;

export interface UseVehicleAlertStationsOptions {
  assetToken: string | undefined;
  period: Period;
  /** Default 5 — matches the EntityLocationMap density on the page. */
  limit?: number;
}

export function useVehicleAlertStations({
  assetToken,
  period,
  limit = 5,
}: UseVehicleAlertStationsOptions): UseQueryResult<VehicleAlertStationsResponse, Error> {
  return useQuery<VehicleAlertStationsResponse, Error>({
    queryKey: ['vehicle-alert-stations', assetToken, period, limit],
    enabled: Boolean(assetToken),
    queryFn: async () => {
      const { data } = await api.get<VehicleAlertStationsResponse>(
        `/vehicles/by-token/${encodeURIComponent(assetToken as string)}/alert-stations`,
        { params: { period, limit } },
      );
      return data;
    },
    staleTime: STALE_MS,
  });
}
